import { useApolloClient } from '@apollo/client';
import { NextRouter } from 'next/router';
import { useEffect } from 'react';
import { isStayDuration } from 'common/utils';
import { ModalPosition } from 'contexts/modalFunc';
import {
  ISearchParams,
  SeatClass,
  TripType,
  useSearchParamsContext,
} from 'contexts/searchParams';
import { getAirportList } from 'hooks/useAirportDetailList';
import { IFlightSearchHistory, useSearchHistories } from 'hooks/useHistory';
import { setFilterStorage } from 'list/international/filter/filterStore';
import domesticFilterStorage from 'storage/domesticFilter';

const SEARCH_HISTORY_COUNT = 1;

const applyHistory = (history: IFlightSearchHistory) => {
  const { tripList, passenger, fareType, isDirect, trip } = history;
  const itinerary = tripList.map(iti => ({
    departureCity: {
      code: iti.departureCityCode,
      name: iti.departureCityName,
      isDomestic: iti.isDomesticDeparture,
    },
    arrivalCity: {
      code: iti.destinationCityCode,
      name: iti.destinationCityName,
      isDomestic: iti.isDomesticDestination,
    },
    departureDate: iti.departureDate,
  }));
  let stayLength: string = '';
  if (trip === TripType.RT && isStayDuration(itinerary[1].departureDate)) {
    stayLength = itinerary[1].departureDate;
    itinerary[1].departureDate = '';
  }
  // do not remove prev
  return (prev: ISearchParams) => ({
    ...prev,
    activeIndex: 0,
    itinerary,
    passengers: {
      adult: passenger.adult || 1,
      child: passenger.child || 0,
      infant: passenger.infant || 0,
    },
    seatClass: (fareType as SeatClass) || SeatClass.Y,
    isDirect: !!isDirect,
    stayLength,
  });
};

function useSearchInitialize(router: NextRouter, setModal: Function) {
  const {
    asPath,
    query: {
      alc,
      aln,
      bizgo,
      searchDetail,
      view,
      departureCity,
      arrivalCity,
      dates,
      trip,
    },
  } = router;
  const apolloClient = useApolloClient();
  const { setSearchParams, setTripType, setDepartureDate, setIsToBizTrip } =
    useSearchParamsContext();
  const { data } = useSearchHistories(SEARCH_HISTORY_COUNT, 'network-only');

  if (alc && aln) {
    const filter = { [alc as string]: aln };
    setFilterStorage({ airlines: filter });
    domesticFilterStorage.set({ airlineCode: filter });
  }

  const hasNxParams = departureCity || arrivalCity || dates || trip;

  async function initializeSearchParams() {
    if (bizgo === 'true') setIsToBizTrip(true);

    if (trip) setTripType(trip as TripType);

    const [departureDate, arrivalDate] = (dates as string)?.split(',') || [];
    if (dates) {
      if (departureDate) setDepartureDate(departureDate, 0);
      if (arrivalDate) setDepartureDate(arrivalDate, 1);
    }

    if (departureCity || arrivalCity) {
      const airportDetailList = await getAirportList(apolloClient, [
        departureCity as string,
        arrivalCity as string,
      ]);
      if (!airportDetailList || !airportDetailList.length) return;
      const cityList = airportDetailList.map(item => ({
        code: item.iataCode,
        name: item.cityName,
        isDomestic: item.isDomestic,
      }));
      const itinerary = [
        { departureCity: cityList[0], arrivalCity: cityList[1], departureDate },
        {
          departureCity: cityList[1],
          arrivalCity: cityList[0],
          departureDate: arrivalDate,
        },
      ];
      const roundTripCityCount = 2;
      if (
        airportDetailList.length < roundTripCityCount &&
        airportDetailList[0].iataCode === arrivalCity
      ) {
        itinerary.reverse();
      }
      setSearchParams(prev => ({
        ...prev,
        itinerary: trip === TripType.OW ? itinerary.slice(0, 1) : itinerary,
      }));
    }

    if (view) {
      setModal({ type: view, position: ModalPosition.MAIN });
    }
  }

  useEffect(() => {
    initializeSearchParams();
    if (alc || aln || bizgo || searchDetail || view || hasNxParams) {
      router.push(asPath, '/', { shallow: true });
    }
  }, []);

  useEffect(() => {
    if (hasNxParams || !data?.searchHistories?.length) return;
    const { searchHistories } = data;
    const recentHistory = searchHistories[0];
    setSearchParams(applyHistory(recentHistory));
    setTripType(recentHistory.trip as TripType);
  }, [hasNxParams, data]);
}

export default useSearchInitialize;
